import { useSnackbar } from "notistack";
import cloneDeep from "lodash.clonedeep";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack } from "@mui/material";

import Header from "../../Header";
import BillingProduct from "../BillingProduct";

import { Dayjs } from "dayjs";
import { api, uuidv4 } from "../../../utils";
import { BoothClientModel, IError } from "../../../models/ReportPageModels";
import { TrackingOptionModel } from "../../Admin/pages/Xero/Tracking/Tracking";

// Models
export interface ProductModel {
    uid: string;
    productCode: string;
    description: string;
    quantity: number;
    price: number;
    tax: number;
    boothIdFilter: string[];
    allowFree: number;
    commission: number[];
    startDate: Dayjs | null;
    portalFeed: number[];
    xeroTracking: TrackingOptionModel | null;
    hide: boolean;
}

const defaultProduct: ProductModel = {
    uid: uuidv4(),
    productCode: "",
    description: "",
    quantity: 1,
    price: -1,
    tax: 1,
    boothIdFilter: [],
    allowFree: 0,
    commission: [],
    startDate: null,
    portalFeed: [],
    xeroTracking: null,
    hide: false,
}

export interface BillingDbProductModel {
    id: number;
    productCode: string;
    description: string;
    databaseLookup: number;
    boothIdFilter: number;
    billingPer: string;
    unitPrice: number;
    taxRate: number;
    allowFree: number;
    allowCommission: number;
    allowHide: number;
}

// ==== Api models
// Clients
interface BillingClientsResponseModel {
    data: BillingClientsData;
    error: IError;
}
interface BillingClientsData {
    clients: string[];
}
// Products
interface BillingProductsResponseModel {
    data: BillingProductsData;
    error: IError;
}
interface BillingProductsData {
    products: BillingDbProductModel[];
}
// Taxes
interface BillingTaxesResponseModel {
    data: BillingTaxesData;
    error: IError;
}
interface BillingTaxesData {
    taxes: BillingTaxModel[];
}
export interface BillingTaxModel {
    id: number;
    percent: number;
    description: string;
}
// BoothId
interface BoothIdResponseModel {
    data: BoothIdData;
    error: IError;
}
interface BoothIdData {
    boothIds: BoothClientModel[];
}
// Get Settings
interface BillingGetSettingsResponseModel {
    data: BillingGetSettingsData;
    error: IError;
}
interface BillingGetSettingsData {
    settings: ProductModel[];
    currency: string;
}
// Commissions
interface BillingCommissionsResponseModel {
    data: BillingCommissionsData;
    error: IError;
}
interface BillingCommissionsData {
    commissions: CommissionModel[];
}
export interface CommissionModel {
    id: number;
    groupNumber: number;
    groupName: string;
    commissionPercent: number;
    salesName: string;
}
// PortalFeed
interface BillingPortalFeedsResponseModel {
    data: BillingPortalFeedsData;
    error: IError;
}
interface BillingPortalFeedsData {
    portalFeeds: DisplayFeedModel[];
}
export interface DisplayFeedModel {
    feedName: string;
    friendlyName: string;
}
// Xero Tracking
interface BillingXeroTrackingResponseModel {
    data: BillingXeroTrackingData;
    error: IError;
}
interface BillingXeroTrackingData {
    options: TrackingOptionModel[];
}

// SaveSettings
interface SaveSettingsRequestBody {
    client: string;
    settings: ProductModel[];
}
interface BillingSaveSettingsResponseModel {
    data: BillingSaveSettingsData;
    error: IError;
}
interface BillingSaveSettingsData {
    status: string;
}

const removeAt = (arr: ProductModel[], i: number): ProductModel[] => {
    return [...arr.slice(0, i), ...arr.slice(i + 1)];
}

const insertAt = (arr: ProductModel[], value: ProductModel, i: number): ProductModel[] => {
    return [...arr.slice(0, i), value, ...arr.slice(i)];
}

function BillingSettings() {
    const { enqueueSnackbar } = useSnackbar();

    const [clients, setClients] = useState<string[]>([]);
    const [products, setProducts] = useState<BillingDbProductModel[]>([]);
    const [productNames, setProductNames] = useState<string[]>([]);
    const [taxes, setTaxes] = useState<BillingTaxModel[]>([]);

    var selectedClientCopy = useRef<string>("");
    const [selectedClient, setSelectedClient] = useState<string>("");

    const [productSettings, setProductSettings] = useState<ProductModel[]>([]);

    const [clientCurrency, setClientCurrency] = useState<string>("");
    const [boothIds, setBoothIds] = useState<BoothClientModel[]>([]);
    const [commission, setCommission] = useState<CommissionModel[]>([])
    const [portalFeeds, setPortalFeeds] = useState<DisplayFeedModel[]>([]);
    const [xeroTracking, setXeroTracking] = useState<TrackingOptionModel[]>([]);

    useEffect(() => {
        GetClientsAndProducts()
    }, [])

    function GetClientsAndProducts() {
        GetClients();
        GetProducts();
        GetTaxes();
    }

    const GetClients = async () => {
        try {
            var resp: BillingClientsResponseModel = await api.get("api/Billing/GetClients", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setClients(resp.data.clients);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetProducts = async () => {
        setProductNames([]);
        setProducts([])
        try {
            var resp: BillingProductsResponseModel = await api.get("api/Billing/GetProducts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                const namesArray: string[] = []
                const productsArray = resp.data.products;

                for (var i = 0; i < productsArray.length; i++) {
                    namesArray.push(productsArray[i].productCode);
                }

                setProductNames(namesArray);
                setProducts(productsArray);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetTaxes = async () => {
        setTaxes([]);
        try {
            var resp: BillingTaxesResponseModel = await api.get("api/Billing/GetTaxes", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setTaxes(resp.data.taxes);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectClient(event: any) {
        var value = event.target.value;

        setProductSettings([]);
        setClientCurrency("");

        selectedClientCopy.current = value;
        setSelectedClient(value);

        GetSettings();

        GetBoothIds();
        GetCommission();
        GetPortalFeeds();
        GetXeroTracking();
    }

    const GetSettings = async () => {
        setProductSettings([]);
        setClientCurrency("");
        try {
            const response: BillingGetSettingsResponseModel = await api.get(`api/Billing/GetSettings?username=${selectedClientCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setProductSettings(response.data.settings);
                setClientCurrency(response.data.currency);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetBoothIds = async () => {
        setBoothIds([]);
        try {
            const response: BoothIdResponseModel = await api.get(`api/Billing/GetBoothIds?username=${selectedClientCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBoothIds(response.data.boothIds);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetCommission = async () => {
        setCommission([]);
        try {
            const response: BillingCommissionsResponseModel = await api.get(`api/Billing/GetCommission?username=${selectedClientCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCommission(response.data.commissions)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetPortalFeeds = async () => {
        setCommission([]);
        try {
            const response: BillingPortalFeedsResponseModel = await api.get(`api/Billing/GetPortalFeeds?username=${selectedClientCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setPortalFeeds(response.data.portalFeeds);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroTracking = async () => {
        setXeroTracking([]);
        try {
            const response: BillingXeroTrackingResponseModel = await api.get(`api/Billing/GetTrackingByClient?username=${selectedClientCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroTracking(response.data.options);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AddProductSettings() {
        const product = cloneDeep(defaultProduct);
        product.uid = uuidv4();
        setProductSettings(array => [...array, product]);
    }

    const SaveSettings = async () => {
        if (selectedClient === "") {
            enqueueSnackbar("Please select a client.", { variant: "error" });
            return;
        }

        var requestObj: SaveSettingsRequestBody = {
            client: selectedClientCopy.current,
            settings: productSettings,
        }
        try {
            const response: BillingSaveSettingsResponseModel = await api.post("api/Billing/SaveSettings", {
                json: requestObj,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Settings saved!", { variant: "success" });
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }



    const RemoveProduct = useCallback((item: ProductModel) => {
        var index = productSettings.indexOf(item);
        if (index === -1)
            return;

        const newProductArray = removeAt(productSettings, index);
        setProductSettings(newProductArray);
    }, [productSettings])

    const DuplicateProduct = useCallback((item: ProductModel) => {
        var index = productSettings.indexOf(item);
        if (index === -1)
            return;

        var newItem = cloneDeep(item);
        newItem.uid = uuidv4();
        const newProductArray = insertAt(productSettings, newItem, index)
        setProductSettings(newProductArray);
    }, [productSettings])

    const MoveUpProduct = useCallback((item: ProductModel) => {
        var index = productSettings.indexOf(item);
        if (index === -1 || index === 0)
            return;

        const newProductArray = insertAt(removeAt(productSettings, index), item, index - 1);
        setProductSettings(newProductArray);
    }, [productSettings])

    const MoveDownProduct = useCallback((item: ProductModel) => {
        var index = productSettings.indexOf(item);
        if (index === -1 || index === productSettings.length - 1)
            return;

        const newProductArray = insertAt(removeAt(productSettings, index), item, index + 1);
        setProductSettings(newProductArray);
    }, [productSettings])

    function GetPanelHeight() {
        return window.innerHeight - 260;
    }

    return (
        <>
            <Header headerType={"BILLING"} displayLogout={false} />
            <Stack direction='row'>
                <Stack
                    direction="column"
                    sx={{ width: "320px" }}
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1.5}>

                    <span>Client</span>
                    <FormControl sx={{ m: 1, width: 240 }}>
                        <Select
                            value={selectedClient}
                            onChange={SelectClient}
                            size="small"
                            defaultValue="">
                            {clients.map((el, index) => (
                                <MenuItem value={el} key={index}>{el}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        sx={{ left: '50px' }}
                        onClick={SaveSettings}>
                        Save settings
                    </Button>
                </Stack>
                <Stack
                    direction='column'
                    sx={{ width: '100%', height: GetPanelHeight(), overflowX: 'auto', overflowY: 'auto', paddingBottom: '150px' }}>
                    <span style={{ marginBottom: "12px" }}>Settings</span>
                    {productSettings.map((product) => (
                        <BillingProduct
                            key={product.uid}

                            allProducts={products}
                            productNames={productNames}

                            clientCurrency={clientCurrency}
                            allTaxes={taxes}
                            allBoothIds={boothIds}
                            allCommission={commission}
                            allPortalFeeds={portalFeeds}
                            allXeroTracking={xeroTracking}

                            product={product}

                            removeProduct={RemoveProduct}
                            duplicateProduct={DuplicateProduct}
                            moveUpProduct={MoveUpProduct}
                            moveDownProduct={MoveDownProduct} />
                    ))}

                    <Button
                        sx={{ width: '50px', height: "40px" }}
                        variant="outlined"
                        onClick={AddProductSettings}>
                        Add
                    </Button>
                </Stack>
            </Stack>
        </>
    )
};

export default BillingSettings;